/**
 * TerrainNode - Grid geometry with fractal height displacement
 *
 * When depth = 0, outputs a flat ground plane.
 * When depth > 0, applies fractal noise to create terrain.
 *
 * Two rendering paths (same as GeometryNode):
 *   SceneNode path — SceneNode calls buildGeometry() and manages the mesh.
 *   Endpoint path  — geometry emitted on geometryOut, flows to materials
 *     via ThreeEndpoint connections (used inside definitions).
 *
 * Animation optimisation:
 *   Terrain geometry is heavy (64-segment plane = 4225 vertices + noise).
 *   Creating a new PlaneGeometry every frame would exhaust memory because
 *   downstream clones share geometry by reference and old objects are never
 *   disposed by the endpoint chain.
 *
 *   Params are split into two categories:
 *     Topology (width, height, segments) — change vertex count, require a
 *       new PlaneGeometry and full re-emit on the endpoint.
 *     Displacement (depth, scale, octaves, persistence, lacunarity, seed) —
 *       only affect vertex Y positions. Updated in-place on the existing
 *       geometry via applyDisplacement() with zero allocation.
 *
 *   A param snapshot detects whether expression params actually changed.
 *   If unchanged, animate() is a no-op.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';

export interface Heightmap {
  data: Float32Array;
  cols: number;
  rows: number;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

const terrainEndpoints = {
  ...internals({
    geometryOut: { dataType: 'three:geometry', isOutput: true },
    heightmapOut: { dataType: 'array', isOutput: true, label: 'Heightmap' },
  }),
  ...params({
    width: { value: 10, min: 1, max: 100, step: 0.1, group: 'Size' },
    height: { value: 10, min: 1, max: 100, step: 0.1, label: 'Length', group: 'Size' },
    segments: { value: 64, min: 2, max: 256, step: 1, group: 'Size' },
    depth: { value: 0, min: 0, max: 10, step: 0.01, group: 'Terrain' },
    scale: { value: 1, min: 0.1, max: 10, step: 0.01, label: 'Noise Scale', group: 'Terrain' },
    octaves: { value: 4, min: 1, max: 8, step: 1, group: 'Terrain' },
    persistence: { value: 0.5, min: 0, max: 1, step: 0.01, group: 'Terrain' },
    lacunarity: { value: 2, min: 1, max: 4, step: 0.1, group: 'Terrain' },
    seed: { value: 0, min: 0, max: 1000, step: 1, group: 'Terrain' }
  })
};

// Simple hash function for pseudo-random values
function hash(x: number, y: number, seed: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7 + seed * 113.5) * 43758.5453;
  return n - Math.floor(n);
}

// Smoothstep interpolation
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

// Value noise with smooth interpolation
function valueNoise(x: number, y: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;

  // Smoothstep the fractional parts
  const u = smoothstep(fx);
  const v = smoothstep(fy);

  // Hash the four corners
  const a = hash(ix, iy, seed);
  const b = hash(ix + 1, iy, seed);
  const c = hash(ix, iy + 1, seed);
  const d = hash(ix + 1, iy + 1, seed);

  // Bilinear interpolation
  return a * (1 - u) * (1 - v) +
         b * u * (1 - v) +
         c * (1 - u) * v +
         d * u * v;
}

// Fractal Brownian Motion
function fbm(
  x: number,
  y: number,
  seed: number,
  octaves: number,
  persistence: number,
  lacunarity: number
): number {
  let total = 0;
  let amplitude = 1;
  let frequency = 1;
  let maxValue = 0;

  for (let i = 0; i < octaves; i++) {
    total += valueNoise(x * frequency, y * frequency, seed + i * 100) * amplitude;
    maxValue += amplitude;
    amplitude *= persistence;
    frequency *= lacunarity;
  }

  // Normalize to 0-1 range, then center around 0 (-0.5 to 0.5)
  return (total / maxValue) - 0.5;
}

class TerrainNode extends BaseNode<typeof terrainEndpoints> {
  private three: Three;
  /** Stored mesh reference from SceneNode — for in-place geometry replacement */
  private mesh: any = null;
  /** Geometry emitted to the endpoint — updated in-place during animation */
  private emittedGeo: any = null;
  /** Topology params (width, height, segments) used to create emittedGeo */
  private lastTopology = '';
  /** Full param snapshot for change detection */
  private lastParamSnapshot = '';

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  initialize(): void {
    super.initialize();
    this.lastParamSnapshot = this.paramSnapshot();
    this.emitGeometry();
  }

  /** Build geometry from current params — called by SceneNode */
  buildGeometry(): any {
    return this.generateTerrain();
  }

  /** Store mesh reference from SceneNode for in-place param mutation */
  setThreeObject(mesh: any): void {
    this.mesh = mesh;
  }

  /** Per-frame: rebuild geometry only when expression params produce new values */
  animate(): void {
    const snapshot = this.paramSnapshot();
    if (snapshot === this.lastParamSnapshot) return;
    this.lastParamSnapshot = snapshot;

    if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }

    // Endpoint path: update existing geometry in-place if topology unchanged
    const topo = this.topologyKey();
    if (this.emittedGeo && topo === this.lastTopology) {
      this.applyDisplacement(this.emittedGeo);
      (this.internals as any).heightmapOut.set(this.buildHeightmap(this.emittedGeo));
    } else {
      this.emitGeometry();
    }
  }

  private paramSnapshot(): string {
    const p = this.params;
    return `${p.width.value},${p.height.value},${p.segments.value},${p.depth.value},${p.scale.value},${p.octaves.value},${p.persistence.value},${p.lacunarity.value},${p.seed.value}`;
  }

  private topologyKey(): string {
    return `${this.params.width.value},${this.params.height.value},${this.params.segments.value}`;
  }

  /** Update vertex Y positions in-place on an existing geometry (no allocation) */
  private applyDisplacement(geometry: any): void {
    const depth = this.params.depth.value;
    const scale = this.params.scale.value;
    const octaves = this.params.octaves.value;
    const persistence = this.params.persistence.value;
    const lacunarity = this.params.lacunarity.value;
    const seed = this.params.seed.value;

    const positions = geometry.attributes.position;
    if (depth > 0) {
      for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i);
        const z = positions.getZ(i);
        positions.setY(i, fbm(x / scale, z / scale, seed, octaves, persistence, lacunarity) * depth);
      }
    } else {
      for (let i = 0; i < positions.count; i++) {
        positions.setY(i, 0);
      }
    }
    positions.needsUpdate = true;
    geometry.computeVertexNormals();
  }

  private generateTerrain(): any {
    const width = this.params.width.value;
    const height = this.params.height.value;
    const segments = this.params.segments.value;
    const depth = this.params.depth.value;
    const scale = this.params.scale.value;
    const octaves = this.params.octaves.value;
    const persistence = this.params.persistence.value;
    const lacunarity = this.params.lacunarity.value;
    const seed = this.params.seed.value;

    // Create plane geometry
    const geometry = new this.three.PlaneGeometry(width, height, segments, segments);

    // Rotate to be horizontal (ground plane)
    geometry.rotateX(-Math.PI / 2);

    // Apply height displacement if depth > 0
    if (depth > 0) {
      const positions = geometry.attributes.position;
      const vertex = new this.three.Vector3();

      for (let i = 0; i < positions.count; i++) {
        vertex.fromBufferAttribute(positions, i);

        // Use x and z for noise (y is up after rotation)
        const nx = vertex.x / scale;
        const nz = vertex.z / scale;

        const heightValue = fbm(nx, nz, seed, octaves, persistence, lacunarity);

        // Apply height (y axis after rotation)
        vertex.y += heightValue * depth;

        positions.setXYZ(i, vertex.x, vertex.y, vertex.z);
      }

      positions.needsUpdate = true;
      geometry.computeVertexNormals();
    }

    return geometry;
  }

  onParamChanged(): void {
    if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
    this.emitGeometry();
  }

  private buildHeightmap(geometry: any): Heightmap {
    const cols = this.params.segments.value + 1;
    const rows = cols;
    const positions = geometry.attributes.position;
    const data = new Float32Array(cols * rows);
    for (let i = 0; i < positions.count; i++) {
      data[i] = positions.getY(i);
    }
    return {
      data, cols, rows,
      minX: -this.params.width.value / 2,
      maxX:  this.params.width.value / 2,
      minZ: -this.params.height.value / 2,
      maxZ:  this.params.height.value / 2,
    };
  }

  private emitGeometry(): void {
    const geo = this.generateTerrain();
    this.emittedGeo = geo;
    this.lastTopology = this.topologyKey();
    (this.internals as any).geometryOut.set({ value: geo });
    (this.internals as any).heightmapOut.set(this.buildHeightmap(geo));
  }
}

export const terrainNodeDef: NodeDef<TerrainNode> = {
  type: 'Terrain',
  shortName: 'terrain',
  info: 'Grid geometry with fractal height displacement. Depth 0 = flat ground plane.',
  category: 'geometry',
  endpoints: terrainEndpoints,
  ctor: TerrainNode
};
